import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import api, { setToken } from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Previsions from "@/pages/Previsions";
import Encaissements from "@/pages/Encaissements";
import Depenses from "@/pages/Depenses";
import Equipe from "@/pages/Equipe";
import Retrocessions from "@/pages/Retrocessions";
import Mouvements from "@/pages/Mouvements";
import Analyses from "@/pages/Analyses";
import Parametres from "@/pages/Parametres";
import Admin from "@/pages/Admin";
import Profils from "@/pages/Profils";
import SecretaireSaisie from "@/pages/SecretaireSaisie";
import PraticienCA from "@/pages/PraticienCA";
import MonProfil from "@/pages/MonProfil";
import RH from "@/pages/RH";
import Maintenance from "@/pages/Maintenance";
import EquipmentPage from "@/pages/EquipmentPage";
import MonCabinet from "@/pages/MonCabinet";
import Conformite from "@/pages/Conformite";
import Factures from "@/pages/Factures";
import Comptabilite from "@/pages/Comptabilite";
import Pilotage from "@/pages/Pilotage";
import TiersPayant from "@/pages/TiersPayant";

const COMP_ROUTE = <Route key="c" path="/conformite" element={<Conformite />} />;

const MAINT_ROUTES = [<Route key="m" path="/maintenance" element={<Maintenance />} />, <Route key="me" path="/maintenance/equipement/:id" element={<EquipmentPage />} />];

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        if (data.token) setToken(data.token);
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      } catch {
        setUser(false);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <Loader label="Connexion en cours…" />;
}

function ProtectedShell() {
  const { user, space } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "secretary")
    return <Layout><Routes><Route path="/saisie" element={<SecretaireSaisie />} />{MAINT_ROUTES}{COMP_ROUTE}<Route path="/profil" element={<MonProfil />} /><Route path="*" element={<Navigate to="/saisie" replace />} /></Routes></Layout>;
  if (user.role === "practitioner" || (user.practitioner_id && space === "praticien"))
    return (
      <Layout>
        <Routes>
          <Route path="/mon-ca" element={<PraticienCA />} />
          <Route path="/mon-cabinet" element={<MonCabinet />} />
          <Route path="/profil" element={<MonProfil />} />
          {MAINT_ROUTES}
          {COMP_ROUTE}
          <Route path="*" element={<Navigate to="/mon-ca" replace />} />
        </Routes>
      </Layout>
    );
  if (user.role === "hr")
    return <Layout><Routes><Route path="/rh" element={<RH />} />{COMP_ROUTE}<Route path="/profil" element={<MonProfil />} /><Route path="*" element={<Navigate to="/rh" replace />} /></Routes></Layout>;
  if (user.role === "hygiene")
    return <Layout><Routes>{COMP_ROUTE}{MAINT_ROUTES}<Route path="/profil" element={<MonProfil />} /><Route path="*" element={<Navigate to="/conformite" replace />} /></Routes></Layout>;
  const isOwner = user.is_superadmin || ["owner", "admin"].includes(user.role);
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/previsions" element={<Previsions />} />
        <Route path="/encaissements" element={<Encaissements />} />
        <Route path="/depenses" element={<Depenses />} />
        <Route path="/factures" element={isOwner ? <Factures /> : <Navigate to="/" replace />} />
        <Route path="/saisie" element={isOwner ? <SecretaireSaisie /> : <Navigate to="/" replace />} />
        <Route path="/comptabilite" element={isOwner ? <Comptabilite /> : <Navigate to="/" replace />} />
        <Route path="/pilotage" element={isOwner ? <Pilotage /> : <Navigate to="/" replace />} />
        <Route path="/tiers-payant" element={isOwner ? <TiersPayant /> : <Navigate to="/" replace />} />
        <Route path="/equipe" element={<Equipe />} />
        <Route path="/retrocessions" element={<Retrocessions />} />
        <Route path="/mouvements" element={<Mouvements />} />
        <Route path="/analyses" element={<Analyses />} />
        <Route path="/parametres" element={<Parametres />} />
        <Route path="/profil" element={<MonProfil />} />
        <Route path="/rh" element={<RH />} />
        {MAINT_ROUTES}
        {COMP_ROUTE}
        <Route path="/profils" element={isOwner ? <Profils /> : <Navigate to="/" replace />} />
        <Route path="/admin" element={user?.is_superadmin ? <Admin /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/*" element={<ProtectedShell />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
